import { FormEvent } from 'react'
import { Page, PageHeader } from '@/components/page'
import { useForm } from '@inertiajs/react'
import { WizardGuide } from '@/components/layout/WizardGuide'
import { WizardLayout } from '../WizardLayout'
import { WizardActions } from '../WizardActions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { CountryCombobox } from '@/components/ui/country-combobox'
import { InternationalPhoneInput } from '@/components/ui/international-phone-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { firstError } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { ArrowRight, MapPin } from 'lucide-react'
import { Tip } from '@/components/sketch/Tip'
import type { WizardFormationRef } from '@/types'

type CountryOption = { code: string; name: string }

type Person = {
  id: string
  principal_kind: string
  full_name: string
  legal_name: string | null
  street_address_1: string | null
  street_address_2: string | null
  city: string | null
  state_province: string | null
  postal_code: string | null
  country_code: string
}

type EinApplication = {
  street_address_1: string
  street_address_2: string | null
  city: string
  state_province: string | null
  postal_code: string
  country_code: string
  phone: string
}

type Props = {
  formation: WizardFormationRef
  people: Person[]
  responsible_party_id: string | null
  responsible_party_tax_id_status: string | null
  responsible_party_tax_identifier_on_file: boolean
  ein_application: EinApplication | null
  countries: CountryOption[]
}

type TaxIdStatus = 'ssn' | 'itin' | 'neither'

export default function TaxSetupsEdit({
  formation, people, responsible_party_id, responsible_party_tax_id_status,
  responsible_party_tax_identifier_on_file, ein_application, countries,
}: Props) {
  const basePath = useBasePath()

  const form = useForm({
    ein_responsible_party_id: responsible_party_id ?? '',
    responsible_party_tax_id_status: (responsible_party_tax_id_status as TaxIdStatus | null) ?? '',
    tax_identifier: '',
    mailing_street_address_1: ein_application?.street_address_1 ?? '',
    mailing_street_address_2: ein_application?.street_address_2 ?? '',
    mailing_city: ein_application?.city ?? '',
    mailing_state_province: ein_application?.state_province ?? '',
    mailing_postal_code: ein_application?.postal_code ?? '',
    mailing_country_code: ein_application?.country_code ?? 'US',
    phone: ein_application?.phone ?? '',
  })

  const status = form.data.responsible_party_tax_id_status as TaxIdStatus | ''
  const needsIdentifier = status === 'ssn' || status === 'itin'
  const responsibleIdentifierOnFile = responsible_party_tax_identifier_on_file &&
    form.data.ein_responsible_party_id === responsible_party_id &&
    status === responsible_party_tax_id_status
  const isUsMailing = form.data.mailing_country_code === 'US'
  const mailingError = firstError(Object.entries(form.errors).find(([key]) => key === 'mailing')?.[1])
  const selectedResponsibleParty = people.find((person) => person.id === form.data.ein_responsible_party_id)
  const responsiblePartyName = selectedResponsibleParty?.full_name || selectedResponsibleParty?.legal_name
  const hasResponsiblePartyAddress = Boolean(
    selectedResponsibleParty?.street_address_1 &&
    selectedResponsibleParty.city &&
    selectedResponsibleParty.postal_code &&
    selectedResponsibleParty.country_code &&
    (selectedResponsibleParty.country_code !== 'US' || selectedResponsibleParty.state_province)
  )

  const useResponsiblePartyAddress = () => {
    if (!selectedResponsibleParty || !hasResponsiblePartyAddress) return

    form.setData({
      ...form.data,
      mailing_street_address_1: selectedResponsibleParty.street_address_1 ?? '',
      mailing_street_address_2: selectedResponsibleParty.street_address_2 ?? '',
      mailing_city: selectedResponsibleParty.city ?? '',
      mailing_state_province: selectedResponsibleParty.state_province ?? '',
      mailing_postal_code: selectedResponsibleParty.postal_code ?? '',
      mailing_country_code: selectedResponsibleParty.country_code,
    })
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    form.patch(`${basePath}/formations/${formation.id}/wizard/tax_setup`)
  }

  return (
    <WizardLayout
      title="EIN details"
      currentStep="tax_setup"
      formationId={formation.id}
      resumeStep={formation.current_wizard_step}
      breadcrumbs={[{ label: 'Businesses', href: 'businesses' }, { label: 'EIN details' }]}
      guide={
        <WizardGuide title="EIN details">
          <p>
            The IRS asks who is responsible for the company and where to send the EIN paperwork. Corppy prepares
            and files the EIN application for you.
          </p>
        </WizardGuide>
      }
      tips={
        <Tip seed="tax-id-never-stored-raw">
          We only use this to file your EIN. Your identifier is encrypted and never shown back in full.
        </Tip>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        <Page>
          <PageHeader
            title={<>EIN details</>}
            subtitle={<>Tell us who is responsible for the company and where to send the EIN paperwork.</>}
          />

          <div className="space-y-1.5 max-w-sm">
            <Label htmlFor="ein_responsible_party_id">Who is the responsible party?</Label>
            <Select
              value={form.data.ein_responsible_party_id}
              onValueChange={(v) => form.setData('ein_responsible_party_id', v)}
            >
              <SelectTrigger id="ein_responsible_party_id" className="w-full" aria-invalid={!!form.errors.ein_responsible_party_id}>
                <SelectValue placeholder="Choose a founder" />
              </SelectTrigger>
              <SelectContent>
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.full_name || p.legal_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.errors.ein_responsible_party_id && (
              <p className="text-sm text-destructive" role="alert">{form.errors.ein_responsible_party_id}</p>
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Does the responsible party have a US tax ID?</p>
            <RadioGroup
              value={form.data.responsible_party_tax_id_status}
              onValueChange={(v) => form.setData('responsible_party_tax_id_status', v)}
              aria-invalid={!!form.errors.responsible_party_tax_id_status}
              className="gap-2"
            >
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="ssn" id="tax-ssn" />
                They have a Social Security Number (SSN)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="itin" id="tax-itin" />
                They have an Individual Taxpayer Identification Number (ITIN)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <RadioGroupItem value="neither" id="tax-neither" />
                Neither — Corppy will still prepare and file the EIN
              </label>
            </RadioGroup>
            {form.errors.responsible_party_tax_id_status && (
              <p className="text-sm text-destructive" role="alert">{form.errors.responsible_party_tax_id_status}</p>
            )}
          </div>

          {needsIdentifier && (
            <div className="space-y-1.5 max-w-[220px]">
              <Label htmlFor="tax_identifier">{status === 'ssn' ? 'Social Security Number' : 'ITIN'}</Label>
              <Input
                id="tax_identifier"
                inputMode="numeric"
                value={form.data.tax_identifier}
                onChange={(e) => form.setData('tax_identifier', e.target.value)}
                placeholder={responsibleIdentifierOnFile ? 'Leave blank to keep saved ID' : '123-45-6789'}
                aria-invalid={!!form.errors.tax_identifier}
              />
              <p className="text-xs text-muted-foreground">
                {responsibleIdentifierOnFile
                  ? 'A tax ID is saved securely. Leave this blank to keep it.'
                  : 'Encrypted at rest and never displayed again.'}
              </p>
              {form.errors.tax_identifier && (
                <p className="text-sm text-destructive" role="alert">{form.errors.tax_identifier}</p>
              )}
            </div>
          )}

          <div className="space-y-4" aria-labelledby="ein-mailing">
            <h2 id="ein-mailing" className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              EIN mailing address
            </h2>
            <div className="max-w-md space-y-4">
              {selectedResponsibleParty && (
                <div className="space-y-2">
                  {hasResponsiblePartyAddress && (
                    <p className="text-sm text-muted-foreground">
                      Use {responsiblePartyName}&apos;s saved address, or enter a different one below.
                    </p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={useResponsiblePartyAddress}
                    disabled={!hasResponsiblePartyAddress}
                    aria-describedby={!hasResponsiblePartyAddress ? 'responsible-party-address-help' : undefined}
                  >
                    <MapPin className="size-4" aria-hidden="true" />
                    Use responsible party’s address
                  </Button>
                  {!hasResponsiblePartyAddress && (
                    <p
                      id="responsible-party-address-help"
                      className="text-sm text-muted-foreground"
                      role="status"
                      aria-live="polite"
                    >
                      This person does not have a complete address on file. Enter the mailing address below.
                    </p>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="mailing_street_address_1">Street address</Label>
                <Input
                  id="mailing_street_address_1"
                  value={form.data.mailing_street_address_1}
                  onChange={(e) => form.setData('mailing_street_address_1', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mailing_street_address_2">
                  Apartment / suite <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="mailing_street_address_2"
                  value={form.data.mailing_street_address_2}
                  onChange={(e) => form.setData('mailing_street_address_2', e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="mailing_city">City</Label>
                  <Input id="mailing_city" value={form.data.mailing_city} onChange={(e) => form.setData('mailing_city', e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mailing_country_code">Country</Label>
                  <CountryCombobox
                    id="mailing_country_code"
                    value={form.data.mailing_country_code}
                    onValueChange={(mailing_country_code) => form.setData('mailing_country_code', mailing_country_code)}
                    countries={countries}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {isUsMailing ? (
                  <div className="space-y-1.5">
                    <Label htmlFor="mailing_state_province">State</Label>
                    <Input
                      id="mailing_state_province"
                      value={form.data.mailing_state_province}
                      onChange={(e) => form.setData('mailing_state_province', e.target.value)}
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label htmlFor="mailing_state_province">
                      State / province <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="mailing_state_province"
                      value={form.data.mailing_state_province}
                      onChange={(e) => form.setData('mailing_state_province', e.target.value)}
                    />
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label htmlFor="mailing_postal_code">Postal code</Label>
                  <Input
                    id="mailing_postal_code"
                    value={form.data.mailing_postal_code}
                    onChange={(e) => form.setData('mailing_postal_code', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone">Contact phone</Label>
                <InternationalPhoneInput
                  id="phone"
                  value={form.data.phone}
                  onValueChange={(phone) => form.setData('phone', phone)}
                  defaultCountryCode={form.data.mailing_country_code}
                />
              </div>
              {mailingError && (
                <p className="text-sm text-destructive" role="alert">{mailingError}</p>
              )}
            </div>
          </div>

          <WizardActions currentStep="tax_setup" formationId={formation.id} disabled={form.processing}>
            <Button type="submit" disabled={form.processing}>
              {form.processing ? 'Saving…' : 'Continue'}
              <ArrowRight className="size-4" />
            </Button>
          </WizardActions>
        </Page>
      </form>
    </WizardLayout>
  )
}
